import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Plus, Minus, Trash2, ShoppingBag } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { inr } from "../lib/api";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";

export const CartDrawer = () => {
  const { cart, isCartOpen, setCartOpen, updateQty, removeFromCart, cartKey, cartSubtotal } = useStore();
  const navigate = useNavigate();

  const goCheckout = () => {
    setCartOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={isCartOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" className="bg-cream w-full sm:max-w-md flex flex-col p-0" data-testid="cart-drawer">
        <SheetTitle className="sr-only">Shopping Cart</SheetTitle>
        <SheetDescription className="sr-only">Review the items in your cart and proceed to checkout.</SheetDescription>
        <div className="px-6 py-5 border-b border-border flex items-center justify-between">
          <h2 className="font-serif text-2xl text-forest">Your Cart</h2>
          <button onClick={() => setCartOpen(false)} data-testid="cart-close"><X size={20} /></button>
        </div>

        {cart.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8 gap-4">
            <ShoppingBag size={48} className="text-muted-foreground/40" />
            <p className="text-muted-foreground">Your cart is empty.</p>
            <Button onClick={() => setCartOpen(false)} className="bg-forest text-cream hover:bg-forest-light rounded-full" data-testid="cart-continue-shopping">
              Continue Shopping
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 no-scrollbar">
              {cart.map((item) => {
                const key = cartKey(item);
                return (
                  <div key={key} className="flex gap-4" data-testid={`cart-item-${item.slug}`}>
                    <img src={item.image} alt={item.name} className="w-20 h-20 rounded-lg object-cover bg-sand" />
                    <div className="flex-1">
                      <div className="flex justify-between gap-2">
                        <Link to={`/product/${item.slug}`} onClick={() => setCartOpen(false)} className="font-serif text-forest leading-tight">{item.name}</Link>
                        <button onClick={() => removeFromCart(key)} data-testid={`cart-remove-${item.slug}`} className="text-muted-foreground hover:text-destructive"><Trash2 size={16} /></button>
                      </div>
                      <div className="text-xs text-muted-foreground">{item.variant}{item.subscription ? " • Subscription" : ""}</div>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center border border-border rounded-full">
                          <button onClick={() => updateQty(key, item.qty - 1)} className="p-1.5" data-testid={`cart-dec-${item.slug}`}><Minus size={14} /></button>
                          <span className="w-6 text-center text-sm">{item.qty}</span>
                          <button onClick={() => updateQty(key, item.qty + 1)} className="p-1.5" data-testid={`cart-inc-${item.slug}`}><Plus size={14} /></button>
                        </div>
                        <span className="text-forest font-medium">{inr(item.price * item.qty)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="border-t border-border px-6 py-5 space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-medium text-forest" data-testid="cart-subtotal">{inr(cartSubtotal)}</span>
              </div>
              <p className="text-xs text-muted-foreground">Delivery & discounts calculated at checkout.</p>
              <Button onClick={goCheckout} data-testid="cart-checkout-button" className="w-full bg-gold text-forest hover:bg-gold-soft rounded-full h-12 text-sm uppercase tracking-[0.15em]">
                Proceed to Checkout
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
